import React from 'react'
import { useTranslation } from 'react-i18next'
import { FileAudio, FileVideo, ImagePlus, Library, Loader2, Trash2, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MediaImg } from '@/components/features/media/MediaImg'
import { MEDIA_RULES, UploadCanceled, checkMediaFile, discardUpload, formatBytes, rememberUpload, uploadMedia } from '@/components/features/media/mediaUpload'
import type { MediaPurpose } from '@/lib/sdk-official/resources/media'
import { cn } from '@/lib/utils'
import { MediaLibraryPicker } from '@/components/features/media/library/MediaLibraryPicker'
import type { MediaLibraryItem } from '@/lib/sdk-official/resources/media'
import { messageOf } from './mediaUtils'

type SlotPurpose = Extract<MediaPurpose, 'episodeAudio' | 'episodeVideo' | 'episodeCover' | 'showCover' | 'feedAudio' | 'feedVideo'>

const ICONS = { episodeAudio: FileAudio, episodeVideo: FileVideo, episodeCover: ImagePlus, showCover: ImagePlus, feedAudio: FileAudio, feedVideo: FileVideo }

/**
 * One file of a show or an episode (audio, video or cover): pick, direct upload with progress and
 * cancel, replace, remove. It only yields the media id; the form saves it on the item.
 */
export const FileSlot = ({ purpose, spaceId, mediaId, fileName, onChange, onBusyChange, onDuration, library = false, error: fieldError, disabled, className }: {
  purpose: SlotPurpose
  spaceId: string
  mediaId: string | null
  /** The name to show for a file already on the item. */
  fileName?: string | null
  onChange: (mediaId: string | null) => void
  onBusyChange?: (busy: boolean) => void
  /** Audio and video: the length read from the picked file, for the episode's duration. */
  onDuration?: (seconds: number) => void
  /** F-178 1b: audio and video also come from the Media library ("Da biblioteca"), reused, never copied. */
  library?: boolean
  error?: string
  disabled?: boolean
  className?: string
}) => {
  const { t, i18n } = useTranslation('media')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const abortRef = React.useRef<AbortController | null>(null)
  const [percent, setPercent] = React.useState<number | null>(null)
  const [error, setError] = React.useState('')
  const [picked, setPicked] = React.useState<string | null>(null)
  const [libraryOpen, setLibraryOpen] = React.useState(false)
  const rule = MEDIA_RULES[purpose]
  const image = rule.kind === 'image'
  const Icon = ICONS[purpose]
  const uploading = percent !== null
  const withLibrary = library && !image

  React.useEffect(() => { onBusyChange?.(uploading) }, [uploading, onBusyChange])
  React.useEffect(() => () => abortRef.current?.abort(), [])

  const readDuration = (file: File) => {
    if (!onDuration || image) return
    const url = URL.createObjectURL(file)
    const el = document.createElement(rule.kind === 'audio' ? 'audio' : 'video')
    el.preload = 'metadata'
    el.onloadedmetadata = () => { if (Number.isFinite(el.duration) && el.duration > 0) onDuration(Math.round(el.duration)); URL.revokeObjectURL(url) }
    el.onerror = () => URL.revokeObjectURL(url)
    el.src = url
  }

  const pick = async (file: File) => {
    const problem = checkMediaFile(purpose, file)
    if (problem) {
      setError(problem === 'tooBig' ? t('upload.tooBig', { max: formatBytes(rule.maxBytes, i18n.language) }) : t(`upload.type.${purpose}`))
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    setError('')
    setPercent(0)
    try {
      const { asset } = await uploadMedia({ purpose, spaceId, file, onProgress: setPercent, signal: controller.signal })
      rememberUpload(asset.id)
      if (mediaId) discardUpload(mediaId)
      setPicked(asset.name || file.name)
      readDuration(file)
      onChange(asset.id)
    } catch (err) {
      setError(err instanceof UploadCanceled ? t('upload.canceled') : messageOf(err, t('upload.failed')))
    } finally {
      setPercent(null)
      abortRef.current = null
    }
  }

  const pickFromLibrary = (item: MediaLibraryItem) => {
    if (mediaId) discardUpload(mediaId)
    setPicked(item.name)
    setError('')
    if (item.durationSeconds) onDuration?.(item.durationSeconds)
    onChange(item.id)
  }

  const remove = () => {
    discardUpload(mediaId)
    setPicked(null)
    setError('')
    onChange(null)
  }

  const shownError = error || fieldError
  const name = picked || fileName

  return (
    <div className={cn('min-w-0 space-y-2', className)} data-testid={`file-slot-${purpose}`}>
      <input ref={inputRef} type="file" accept={rule.accept} className="hidden" disabled={disabled}
        onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void pick(file) }} />
      {mediaId && !uploading ? (
        <div className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-surface p-2">
          {image
            ? <MediaImg mediaId={mediaId} variant="thumb" className="h-16 w-16 flex-shrink-0 rounded-md object-cover" alt="" />
            : <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary"><Icon className="h-6 w-6" /></span>}
          <span className="min-w-0 flex-1 truncate text-sm text-foreground-secondary">{name || t(`upload.attached.${purpose}`)}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>{t('upload.replace')}</Button>
          <Button type="button" variant="ghost" size="sm" onClick={remove} disabled={disabled} aria-label={t('upload.remove')}><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      ) : uploading ? (
        <div className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-surface p-3" role="status">
          <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground-secondary">{t('upload.uploading', { percent })}</p>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${percent}%` }} /></div>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => abortRef.current?.abort()} aria-label={t('upload.cancel')}><X className="h-4 w-4" /></Button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled}
          className="flex w-full min-w-0 flex-col items-center gap-1 rounded-lg border-2 border-dashed border-border bg-surface px-4 py-5 text-center hover:border-primary hover:bg-primary-50/40 disabled:opacity-60">
          <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
          <span className="text-sm font-medium text-foreground-secondary">{t(`upload.choose.${purpose}`)}</span>
          <span className="text-xs text-muted-foreground">{t(`upload.limits.${purpose}`, { max: formatBytes(rule.maxBytes, i18n.language) })}</span>
        </button>
      )}
      {withLibrary && !uploading && (
        <Button type="button" variant="ghost" size="sm" onClick={() => setLibraryOpen(true)} disabled={disabled} data-testid={`file-slot-library-${purpose}`}>
          <Library className="mr-1.5 h-4 w-4" aria-hidden />{t(mediaId ? 'library.replaceFromLibrary' : 'library.fromLibrary')}
        </Button>
      )}
      {shownError && <p role="alert" className="text-sm text-red-600">{shownError}</p>}
      {withLibrary && <MediaLibraryPicker open={libraryOpen} onOpenChange={setLibraryOpen} kind={rule.kind === 'audio' ? 'audio' : 'video'} target={purpose.startsWith('feed') ? 'status' : 'episode'} onPick={pickFromLibrary} />}
    </div>
  )
}
